import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Youtube from '@tiptap/extension-youtube';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Plus, Trash2, Pencil, Power, PowerOff, Send, Clock, Zap,
  FileText, ChevronDown, ChevronUp, Copy, Loader2, Eye,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Link as LinkIcon,
  Image as ImageIcon, Video, Heading1, Heading2, Undo2, Redo2,
  Users, BarChart3,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_LABELS = {
  quiz_completed: 'Quiz Completed',
  report_purchased: 'Report Purchased',
  coupon_used: 'Coupon Code Used',
  no_purchase_24h: 'No Purchase After 24h',
  quiz_abandoned_48h: 'Quiz Abandoned (48h)',
};

const DELAY_OPTIONS = [
  { value: 0, label: 'Immediately' },
  { value: 60, label: '1 hour' },
  { value: 360, label: '6 hours' },
  { value: 1440, label: '24 hours' },
  { value: 2880, label: '48 hours' },
  { value: 10080, label: '7 days' },
];

const VARIABLES = [
  { key: 'name', desc: 'User name' },
  { key: 'company', desc: 'Company name' },
  { key: 'archetype_primary', desc: 'Primary archetype' },
  { key: 'archetype_secondary', desc: 'Secondary archetype' },
  { key: 'coupon_code', desc: 'Coupon code used' },
];

// ── Tiptap Toolbar ────────────────────────────────────────────────

function EditorToolbar({ editor }) {
  if (!editor) return null;

  const addImage = () => {
    const url = window.prompt('Enter image / GIF URL:');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const addLink = () => {
    const url = window.prompt('Enter link URL:');
    if (url) editor.chain().focus().setLink({ href: url }).run();
    else editor.chain().focus().unsetLink().run();
  };

  const addVideo = () => {
    const url = window.prompt('Enter YouTube video URL:');
    if (url) editor.commands.setYoutubeVideo({ src: url, width: 480, height: 270 });
  };

  const btn = (active, onClick, icon, title) => (
    <button type="button" onClick={onClick} title={title}
      className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${active ? 'bg-gray-200 text-purple-700' : 'text-gray-600'}`}>
      {icon}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5 bg-gray-50 rounded-t-lg" data-testid="editor-toolbar">
      {btn(false, () => editor.chain().focus().undo().run(), <Undo2 className="h-4 w-4" />, 'Undo')}
      {btn(false, () => editor.chain().focus().redo().run(), <Redo2 className="h-4 w-4" />, 'Redo')}
      <span className="w-px h-5 bg-gray-300 mx-1" />
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), <Heading1 className="h-4 w-4" />, 'Heading 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <Heading2 className="h-4 w-4" />, 'Heading 2')}
      <span className="w-px h-5 bg-gray-300 mx-1" />
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <Bold className="h-4 w-4" />, 'Bold')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <Italic className="h-4 w-4" />, 'Italic')}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), <UnderlineIcon className="h-4 w-4" />, 'Underline')}
      <span className="w-px h-5 bg-gray-300 mx-1" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), <List className="h-4 w-4" />, 'Bullet List')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered className="h-4 w-4" />, 'Ordered List')}
      <span className="w-px h-5 bg-gray-300 mx-1" />
      {btn(editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(), <AlignLeft className="h-4 w-4" />, 'Align Left')}
      {btn(editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(), <AlignCenter className="h-4 w-4" />, 'Align Center')}
      {btn(editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(), <AlignRight className="h-4 w-4" />, 'Align Right')}
      <span className="w-px h-5 bg-gray-300 mx-1" />
      {btn(editor.isActive('link'), addLink, <LinkIcon className="h-4 w-4" />, 'Add Link')}
      {btn(false, addImage, <ImageIcon className="h-4 w-4" />, 'Add Image / GIF')}
      {btn(false, addVideo, <Video className="h-4 w-4" />, 'Add YouTube Video')}
      <span className="w-px h-5 bg-gray-300 mx-1" />
      <input type="color" className="w-6 h-6 border-0 cursor-pointer rounded" title="Text Color"
        onChange={e => editor.chain().focus().setColor(e.target.value).run()}
        data-testid="text-color-picker" />
    </div>
  );
}


// ── Template Editor Dialog ────────────────────────────────────────

function TemplateEditor({ open, onClose, onSave, initial }) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] } }),
      Underline,
      TextStyle,
      Color,
      Image.configure({ inline: true }),
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Youtube.configure({ inline: false }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose max-w-none min-h-[250px] p-4 focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (open && editor) {
      if (initial) {
        setName(initial.name || '');
        setSubject(initial.subject || '');
        editor.commands.setContent(initial.body_html || '');
      } else {
        setName(''); setSubject('');
        editor.commands.setContent('');
      }
    }
  }, [initial, open, editor]);

  const insertVariable = (varKey) => {
    if (editor) editor.chain().focus().insertContent(`{{${varKey}}}`).run();
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim()) {
      toast.error('Name and subject are required');
      return;
    }
    setSaving(true);
    await onSave({ name, subject, body_html: editor?.getHTML() || '' });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="template-editor-title">
            {initial ? 'Edit Template' : 'New Email Template'}
          </DialogTitle>
          <DialogDescription>
            Use {'{{variable}}'} syntax to insert dynamic content.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label>Template Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Welcome Email" data-testid="template-name-input" />
          </div>
          <div>
            <Label>Subject Line</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Welcome {{name}}!" data-testid="template-subject-input" />
          </div>

          {/* Variable Chips */}
          <div>
            <Label className="mb-2 block">Insert Variable</Label>
            <div className="flex flex-wrap gap-2">
              {VARIABLES.map(v => (
                <button
                  key={v.key}
                  onClick={() => insertVariable(v.key)}
                  className="text-xs px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 font-mono transition-colors"
                  title={v.desc}
                  data-testid={`var-chip-${v.key}`}
                >
                  {`{{${v.key}}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Rich Text Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Email Body</Label>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(!showPreview)} data-testid="preview-toggle-btn">
                <Eye className="h-4 w-4 mr-1" /> {showPreview ? 'Editor' : 'Preview'}
              </Button>
            </div>
            {showPreview ? (
              <div className="border rounded-lg p-4 min-h-[300px] prose max-w-none" data-testid="template-preview"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editor?.getHTML() || '', { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target'] }) }} />
            ) : (
              <div className="border rounded-lg overflow-hidden" data-testid="template-body-editor">
                <EditorToolbar editor={editor} />
                <EditorContent editor={editor} />
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
              <strong>Click tracking:</strong> add <code className="bg-gray-100 px-1.5 py-0.5 rounded">data-track=&quot;true&quot;</code> to any{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">&lt;a&gt;</code> tag (via the source-HTML view) to track its clicks.
              Opens are tracked automatically. An unsubscribe link is appended to every email.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose} data-testid="template-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="template-save-btn"
            style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
            className="text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {initial ? 'Update' : 'Create'} Template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Rule Editor Dialog ────────────────────────────────────────────

function RuleEditor({ open, onClose, onSave, initial, templates }) {
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('quiz_completed');
  const [templateId, setTemplateId] = useState('');
  const [delay, setDelay] = useState(0);
  const [condCoupon, setCondCoupon] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setName(initial.name || '');
      setTrigger(initial.trigger || 'quiz_completed');
      setTemplateId(initial.template_id || '');
      setDelay(initial.delay_minutes ?? 0);
      setCondCoupon(initial.condition?.coupon_code || '');
    } else {
      setName(''); setTrigger('quiz_completed'); setTemplateId(''); setDelay(0); setCondCoupon('');
    }
  }, [initial, open]);

  const handleSave = async () => {
    if (!name.trim() || !templateId) {
      toast.error('Name and template are required');
      return;
    }
    setSaving(true);
    const condition = condCoupon.trim() ? { coupon_code: condCoupon.trim().toUpperCase() } : {};
    await onSave({ name, trigger, template_id: templateId, delay_minutes: delay, condition });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle data-testid="rule-editor-title">{initial ? 'Edit Rule' : 'New Automation Rule'}</DialogTitle>
          <DialogDescription>Define when and what email to send automatically.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label>Rule Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Post-Quiz Welcome" data-testid="rule-name-input" />
          </div>
          <div>
            <Label>Trigger Event</Label>
            <select value={trigger} onChange={e => setTrigger(e.target.value)}
              className="w-full h-10 border rounded-md px-3 text-sm" data-testid="rule-trigger-select">
              {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {(trigger === 'coupon_used' || trigger === 'report_purchased') && (
            <div>
              <Label>Condition: Coupon Code (optional)</Label>
              <Input value={condCoupon} onChange={e => setCondCoupon(e.target.value.toUpperCase())}
                placeholder="e.g. BRAND50 (leave empty for any)" className="uppercase font-mono"
                data-testid="rule-condition-coupon" />
            </div>
          )}

          <div>
            <Label>Delay</Label>
            <select value={delay} onChange={e => setDelay(Number(e.target.value))}
              className="w-full h-10 border rounded-md px-3 text-sm" data-testid="rule-delay-select">
              {DELAY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label>Email Template</Label>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)}
              className="w-full h-10 border rounded-md px-3 text-sm" data-testid="rule-template-select">
              <option value="">Select a template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose} data-testid="rule-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="rule-save-btn"
            style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
            className="text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {initial ? 'Update' : 'Create'} Rule
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Main Tab Component ────────────────────────────────────────────

export function AdminEmailAutomationTab() {
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [queue, setQueue] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sub-tab navigation
  const [activeSection, setActiveSection] = useState('templates');

  // Dialogs
  const [tplEditorOpen, setTplEditorOpen] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const [ruleEditorOpen, setRuleEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [historyEmail, setHistoryEmail] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);

  // Contacts state
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [contactSearch, setContactSearch] = useState('');
  const [filterPurchased, setFilterPurchased] = useState('all');
  const [filterArchetype, setFilterArchetype] = useState('all');
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [isBulkSending, setIsBulkSending] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tRes, rRes, qRes, cRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/email-automation/templates`),
        fetch(`${API_URL}/api/admin/email-automation/rules`),
        fetch(`${API_URL}/api/admin/email-automation/queue`),
        fetch(`${API_URL}/api/admin/email-automation/contacts`),
        fetch(`${API_URL}/api/admin/email-automation/stats`),
      ]);
      setTemplates(await tRes.json());
      setRules(await rRes.json());
      setQueue(await qRes.json());
      setContacts(await cRes.json());
      setStats(await sRes.json());
    } catch {
      toast.error('Failed to load email automation data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Template CRUD ──
  const handleSaveTemplate = async (data) => {
    const isEdit = !!editingTpl;
    const url = isEdit
      ? `${API_URL}/api/admin/email-automation/templates/${editingTpl.id}`
      : `${API_URL}/api/admin/email-automation/templates`;
    const res = await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (res.ok) {
      toast.success(isEdit ? 'Template updated' : 'Template created');
      setTplEditorOpen(false);
      setEditingTpl(null);
      fetchData();
    } else {
      const err = await res.json();
      toast.error(err.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    const res = await fetch(`${API_URL}/api/admin/email-automation/templates/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Template deleted'); fetchData(); }
    else { const err = await res.json(); toast.error(err.detail || 'Failed to delete'); }
  };

  const handleDuplicateTemplate = async (tpl) => {
    await handleSaveTemplate({ name: `${tpl.name} (copy)`, subject: tpl.subject, body_html: tpl.body_html });
  };

  // ── Rule CRUD ──
  const handleSaveRule = async (data) => {
    const isEdit = !!editingRule;
    const url = isEdit
      ? `${API_URL}/api/admin/email-automation/rules/${editingRule.id}`
      : `${API_URL}/api/admin/email-automation/rules`;
    const res = await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (res.ok) {
      toast.success(isEdit ? 'Rule updated' : 'Rule created');
      setRuleEditorOpen(false);
      setEditingRule(null);
      fetchData();
    } else {
      const err = await res.json();
      toast.error(err.detail || 'Failed to save rule');
    }
  };

  const handleToggleRule = async (id) => {
    const res = await fetch(`${API_URL}/api/admin/email-automation/rules/${id}/toggle`, { method: 'PATCH' });
    if (res.ok) { fetchData(); }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    const res = await fetch(`${API_URL}/api/admin/email-automation/rules/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Rule deleted'); fetchData(); }
    else { const err = await res.json(); toast.error(err.detail || 'Failed to delete'); }
  };

  // ── Contacts ──
  const handleBulkSend = async () => {
    if (!bulkTemplateId) { toast.error('Select a template first'); return; }
    if (selectedContacts.size === 0) { toast.error('Select at least one contact'); return; }
    if (!window.confirm(`Send email to ${selectedContacts.size} contacts?`)) return;
    setIsBulkSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-automation/bulk-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: bulkTemplateId, contact_emails: Array.from(selectedContacts) }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Sent: ${data.sent}, Failed: ${data.failed}`);
        setSelectedContacts(new Set());
        fetchData();
      } else {
        toast.error(data.detail || 'Bulk send failed');
      }
    } catch { toast.error('Network error'); }
    setIsBulkSending(false);
  };

  const handleViewHistory = async (email) => {
    setHistoryEmail(email);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-automation/contacts/${encodeURIComponent(email)}/history`);
      setHistoryItems(await res.json());
    } catch { setHistoryItems([]); }
  };

  const toggleContactSelect = (email) => {
    setSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const filteredContacts = contacts.filter(c => {
    if (contactSearch && !c.email.includes(contactSearch.toLowerCase()) && !c.name.toLowerCase().includes(contactSearch.toLowerCase())) return false;
    if (filterPurchased === 'paid' && !c.purchased) return false;
    if (filterPurchased === 'unpaid' && c.purchased) return false;
    if (filterArchetype !== 'all' && c.primary_archetype !== filterArchetype) return false;
    return true;
  });

  const selectAllVisible = () => {
    const emails = filteredContacts.map(c => c.email);
    setSelectedContacts(new Set(emails));
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>;

  const templateMap = Object.fromEntries(templates.map(t => [t.id, t]));
  const archetypes = [...new Set(contacts.map(c => c.primary_archetype).filter(Boolean))].sort();

  const SUB_TABS = [
    { key: 'templates', label: 'Templates', icon: <FileText className="h-4 w-4" /> },
    { key: 'rules', label: 'Rules', icon: <Zap className="h-4 w-4" /> },
    { key: 'contacts', label: `Contacts (${contacts.length})`, icon: <Users className="h-4 w-4" /> },
    { key: 'logs', label: `Logs & Stats`, icon: <BarChart3 className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-6" data-testid="email-automation-tab">

      {/* Sub-tab navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-testid="email-sub-tabs">
        {SUB_TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveSection(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeSection === tab.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`subtab-${tab.key}`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ── Templates Section ── */}
      {activeSection === 'templates' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" /> Email Templates
            </h3>
            <Button size="sm" onClick={() => { setEditingTpl(null); setTplEditorOpen(true); }}
              style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
              className="text-white" data-testid="new-template-btn">
              <Plus className="h-4 w-4 mr-1" /> New Template
            </Button>
          </div>
          {templates.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-gray-500">No templates yet. Create one to get started.</CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {templates.map(tpl => (
                <Card key={tpl.id} data-testid={`template-card-${tpl.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{tpl.name}</p>
                      <p className="text-sm text-gray-500 truncate">Subject: {tpl.subject}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <Button variant="ghost" size="icon" title="Duplicate" onClick={() => handleDuplicateTemplate(tpl)} data-testid={`dup-tpl-${tpl.id}`}><Copy className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => { setEditingTpl(tpl); setTplEditorOpen(true); }} data-testid={`edit-tpl-${tpl.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Delete" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteTemplate(tpl.id)} data-testid={`del-tpl-${tpl.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Rules Section ── */}
      {activeSection === 'rules' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Zap className="h-5 w-5 text-cyan-500" /> Automation Rules
            </h3>
            <Button size="sm" onClick={() => { setEditingRule(null); setRuleEditorOpen(true); }}
              disabled={templates.length === 0}
              style={templates.length > 0 ? { background: 'linear-gradient(to right, #7A00EB, #00c3ef)' } : {}}
              className={templates.length > 0 ? 'text-white' : ''}
              data-testid="new-rule-btn">
              <Plus className="h-4 w-4 mr-1" /> New Rule
            </Button>
          </div>
          {templates.length === 0 && (
            <Card><CardContent className="py-6 text-center text-gray-500">Create an email template first to set up automation rules.</CardContent></Card>
          )}
          {rules.length === 0 && templates.length > 0 ? (
            <Card><CardContent className="py-10 text-center text-gray-500">No automation rules yet.</CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {rules.map(rule => {
                const tpl = templateMap[rule.template_id];
                return (
                  <Card key={rule.id} className={!rule.active ? 'opacity-60' : ''} data-testid={`rule-card-${rule.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${rule.active ? 'bg-green-500' : 'bg-gray-400'}`} />
                            <p className="font-semibold text-gray-900 truncate">{rule.name}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">{TRIGGER_LABELS[rule.trigger] || rule.trigger}</span>
                            {rule.delay_minutes > 0 && (
                              <span className="bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                <Clock className="h-3 w-3" /> {DELAY_OPTIONS.find(o => o.value === rule.delay_minutes)?.label || `${rule.delay_minutes}m`}
                              </span>
                            )}
                            {rule.condition?.coupon_code && (
                              <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-mono font-medium">Coupon: {rule.condition.coupon_code}</span>
                            )}
                            <span className="text-gray-400">&rarr;</span>
                            <span className="text-gray-600 truncate max-w-[200px]">{tpl?.name || 'Unknown template'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                          <Button variant="ghost" size="icon" title={rule.active ? 'Deactivate' : 'Activate'} onClick={() => handleToggleRule(rule.id)} data-testid={`toggle-rule-${rule.id}`}>
                            {rule.active ? <Power className="h-4 w-4 text-green-600" /> : <PowerOff className="h-4 w-4 text-gray-400" />}
                          </Button>
                          <Button variant="ghost" size="icon" title="Edit" onClick={() => { setEditingRule(rule); setRuleEditorOpen(true); }} data-testid={`edit-rule-${rule.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" title="Delete" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteRule(rule.id)} data-testid={`del-rule-${rule.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Contacts Section ── */}
      {activeSection === 'contacts' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" /> Contacts
            </h3>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4" data-testid="contact-filters">
            <Input placeholder="Search name or email..." value={contactSearch}
              onChange={e => setContactSearch(e.target.value)}
              className="w-64 h-9 text-sm" data-testid="contact-search" />
            <select value={filterPurchased} onChange={e => setFilterPurchased(e.target.value)}
              className="h-9 border rounded-md px-3 text-sm" data-testid="filter-purchased">
              <option value="all">All</option>
              <option value="paid">Purchased</option>
              <option value="unpaid">Not Purchased</option>
            </select>
            <select value={filterArchetype} onChange={e => setFilterArchetype(e.target.value)}
              className="h-9 border rounded-md px-3 text-sm" data-testid="filter-archetype">
              <option value="all">All Archetypes</option>
              {archetypes.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          {/* Bulk actions bar */}
          {selectedContacts.size > 0 && (
            <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4" data-testid="bulk-action-bar">
              <span className="text-sm font-medium text-purple-800">{selectedContacts.size} selected</span>
              <select value={bulkTemplateId} onChange={e => setBulkTemplateId(e.target.value)}
                className="h-9 border rounded-md px-3 text-sm flex-1 max-w-xs" data-testid="bulk-template-select">
                <option value="">Select template...</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <Button size="sm" onClick={handleBulkSend} disabled={isBulkSending || !bulkTemplateId}
                style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
                className="text-white" data-testid="bulk-send-btn">
                {isBulkSending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                Send to Selected
              </Button>
              <button onClick={() => setSelectedContacts(new Set())} className="text-sm text-gray-500 hover:text-gray-700 underline">Clear</button>
            </div>
          )}

          {/* Contact table */}
          <div className="border rounded-lg overflow-hidden">
            {filteredContacts.length === 0 ? (
              <div className="p-10 text-center text-gray-500 text-sm">No contacts found.</div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-3 w-10"><input type="checkbox" onChange={e => e.target.checked ? selectAllVisible() : setSelectedContacts(new Set())}
                        checked={selectedContacts.size > 0 && selectedContacts.size === filteredContacts.length} data-testid="select-all-contacts" /></th>
                      <th className="text-left p-3 font-medium text-gray-600">Contact</th>
                      <th className="text-left p-3 font-medium text-gray-600">Archetype</th>
                      <th className="text-left p-3 font-medium text-gray-600">Status</th>
                      <th className="text-left p-3 font-medium text-gray-600">Coupon</th>
                      <th className="text-left p-3 font-medium text-gray-600">Emails</th>
                      <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map(c => (
                      <tr key={c.email} className="border-t hover:bg-gray-50" data-testid={`contact-row-${c.email}`}>
                        <td className="p-3"><input type="checkbox" checked={selectedContacts.has(c.email)}
                          onChange={() => toggleContactSelect(c.email)} /></td>
                        <td className="p-3">
                          <p className="font-medium text-gray-900 truncate max-w-[200px]">{c.name || '—'}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[200px]">{c.email}</p>
                        </td>
                        <td className="p-3">
                          {c.primary_archetype && (
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium">{c.primary_archetype}</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.purchased ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {c.purchased ? 'Paid' : 'Free'}
                          </span>
                        </td>
                        <td className="p-3 text-xs font-mono text-gray-600">{c.coupon_used || '—'}</td>
                        <td className="p-3 text-xs text-gray-500">{c.emails_sent}</td>
                        <td className="p-3">
                          <Button variant="ghost" size="sm" onClick={() => handleViewHistory(c.email)} data-testid={`view-history-${c.email}`}>
                            <Clock className="h-3.5 w-3.5 mr-1" /> History
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Logs & Stats Section ── */}
      {activeSection === 'logs' && (
        <div className="space-y-6">
          {/* Stats cards */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="email-stats">
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{stats.sent}</p>
                <p className="text-xs text-gray-500">Total Sent</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.sent_today}</p>
                <p className="text-xs text-gray-500">Sent Today</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-500">{stats.failed}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </CardContent></Card>
            </div>
          )}

          {/* Engagement cards */}
          {stats?.engagement && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="email-engagement">
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.engagement.open_rate}%</p>
                <p className="text-xs text-gray-500">Open Rate</p>
                <p className="text-[10px] text-gray-400">{stats.engagement.opened} / {stats.engagement.tracked}</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.engagement.click_rate}%</p>
                <p className="text-xs text-gray-500">Click Rate</p>
                <p className="text-[10px] text-gray-400">{stats.engagement.clicked} / {stats.engagement.tracked}</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-800">{stats.engagement.tracked}</p>
                <p className="text-xs text-gray-500">Tracked Sends</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{stats.engagement.unsubscribed}</p>
                <p className="text-xs text-gray-500">Unsubscribed</p>
              </CardContent></Card>
            </div>
          )}

          {/* Per-rule performance */}
          {stats?.per_rule?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Performance by Rule</h4>
              <div className="grid gap-2">
                {stats.per_rule.map((r) => (
                  <div key={`rule-${r.rule}`} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5">
                    <span className="text-sm font-medium text-gray-800 truncate">{r.rule}</span>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-green-600 font-medium">{r.sent} sent</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-gray-500">{r.total} total</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Daily volume chart (simple bar) */}
          {stats?.daily?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Daily Send Volume (14 days)</h4>
              <div className="flex items-end gap-1 h-32 bg-gray-50 rounded-lg p-3" data-testid="daily-chart">
                {stats.daily.map((d) => {
                  const max = Math.max(...stats.daily.map(x => x.total), 1);
                  const h = (d.total / max) * 100;
                  return (
                    <div key={`day-${d.date}`} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.sent} sent / ${d.total} total`}>
                      <div className="w-full rounded-t" style={{ height: `${Math.max(h, 4)}%`, background: 'linear-gradient(to top, #7A00EB, #00c3ef)' }} />
                      <span className="text-[9px] text-gray-400">{d.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Full email log */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Send className="h-4 w-4" /> Email Log ({queue.length})
            </h4>
            <div className="border rounded-lg overflow-hidden">
              {queue.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">No emails sent yet.</div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-600">Recipient</th>
                        <th className="text-left p-3 font-medium text-gray-600">Rule</th>
                        <th className="text-left p-3 font-medium text-gray-600">Scheduled</th>
                        <th className="text-left p-3 font-medium text-gray-600">Status</th>
                        <th className="text-center p-3 font-medium text-gray-600">Opens</th>
                        <th className="text-center p-3 font-medium text-gray-600">Clicks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queue.map(item => (
                        <tr key={item.id} className="border-t" data-testid={`queue-item-${item.id}`}>
                          <td className="p-3 truncate max-w-[200px]">
                            {item.recipient_email}
                            {item.unsubscribed && (
                              <span className="ml-2 text-[10px] uppercase tracking-wide text-orange-600 font-semibold" title="Recipient has unsubscribed">unsub</span>
                            )}
                          </td>
                          <td className="p-3 truncate max-w-[150px]">{item.rule_name || item.rule_id}</td>
                          <td className="p-3 whitespace-nowrap text-gray-500">{new Date(item.scheduled_at).toLocaleString()}</td>
                          <td className="p-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              item.status === 'sent' ? 'bg-green-100 text-green-700' :
                              item.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>{item.status}</span>
                          </td>
                          <td className="p-3 text-center">
                            {item.opened
                              ? <span className="text-purple-600 font-medium" title={`Opened ${item.open_count} time(s)`}>✓ {item.open_count}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="p-3 text-center">
                            {item.click_count > 0
                              ? <span className="text-blue-600 font-medium">{item.click_count}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Contact History Dialog ── */}
      <Dialog open={!!historyEmail} onOpenChange={() => setHistoryEmail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Email History</DialogTitle>
            <DialogDescription>{historyEmail}</DialogDescription>
          </DialogHeader>
          {historyItems.length === 0 ? (
            <p className="text-center text-gray-500 py-6 text-sm">No emails sent to this contact yet.</p>
          ) : (
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {historyItems.map(item => (
                <div key={item.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{item.rule_name || 'Manual'}</p>
                    <p className="text-xs text-gray-500">{new Date(item.scheduled_at).toLocaleString()}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    item.status === 'sent' ? 'bg-green-100 text-green-700' :
                    item.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>{item.status}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template & Rule Editor Dialogs */}
      <TemplateEditor open={tplEditorOpen} onClose={() => { setTplEditorOpen(false); setEditingTpl(null); }}
        onSave={handleSaveTemplate} initial={editingTpl} />
      <RuleEditor open={ruleEditorOpen} onClose={() => { setRuleEditorOpen(false); setEditingRule(null); }}
        onSave={handleSaveRule} initial={editingRule} templates={templates} />
    </div>
  );
}
